import React, { useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import GridItem from '../../../../layout/GridComponents/GridItem';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Rectangle,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { greys, mainColors } from '../../../../../styling/theme';
import { percentageToNdecialPlaces } from '../../../../../utilities/numberFormatters';
import { DataObject } from '../../../../../types/redux/data/dataTypes';
import { useDispatch } from 'react-redux';
import usePngFromRecharts from '../../../../../hooks/usePngFromRecharts';
import { addComponentToPdfExport } from '../../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../../types/redux/pdfExports/FileExportsStore';
import FileSaver from 'file-saver';
import ExportButton from '../../../../feedback/ExportButton';
import ChartDownloadButton from '../../../../buttons/ChartDownloadButton';

interface ExposureVsCumulativeChartProps {
  data: any;
}

interface ExposureVsCumulativeChartData {
  sector: string;
  gross_exposure_pct: number;
  pl_pct: number;
}

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0.62rem 1.56rem 0.62rem 0.94rem',
  },
  cardTitle: {
    color: mainColors.mainBlue,
    fontSize: '1.38rem',
    fontWeight: 400,
    margin: '0.62rem 0.94rem',
  },
  customTooltipContainer: {
    backgroundColor: 'white',
    padding: '0.62rem 0.94rem',
    borderRadius: '0.31rem',
    border: `1px solid ${mainColors.mainBlue}`,
    width: '15.62rem',
    color: mainColors.mainBlue,
  },
  tooltipName: {
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  tooltipGrossExposure: {
    fontSize: '1.00rem',
    fontWeight: 600,
    color: mainColors.mainBlue,
  },
  tooltipPLPositive: {
    fontSize: '1.00rem',
    fontWeight: 600,
    color: mainColors.Pass,
  },
  tooltipPLNegative: {
    fontSize: '1.00rem',
    fontWeight: 600,
    color: mainColors.Fail,
  },
  exportButtons: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const CustomTooltip = ({ active, payload, label }: any) => {
  const classes = useStyles();
  if (active && payload && payload.length) {
    return (
      <div className={classes.customTooltipContainer}>
        <div>
          <div className={classes.tooltipName}>{label}</div>
          <hr />
          <div className={classes.tooltipGrossExposure}>
            Gross Exposure:{' '}
            {percentageToNdecialPlaces(
              payload.find((item: any) => item.name === 'gross_exposure_pct')
                .value / 100,
              2,
            )}
          </div>
          <div
            className={
              payload.find((item: any) => item.name === 'pl_pct').value < 0
                ? classes.tooltipPLNegative
                : classes.tooltipPLPositive
            }
          >
            P/L:{' '}
            {percentageToNdecialPlaces(
              payload.find((item: any) => item.name === 'pl_pct').value / 100,
              2,
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

const CustomPLBar = (props: any) => {
  let fill = props.fill;

  if (props.value >= 0) {
    fill = mainColors.Pass;
  } else {
    fill = mainColors.Fail;
  }

  return <Rectangle {...props} fill={fill} />;
};

const buildChartData = (
  inputData: DataObject,
): ExposureVsCumulativeChartData[] => {
  if (!inputData.data || !inputData.data.length) {
    return [];
  } else {
    const returnData: ExposureVsCumulativeChartData[] = [];
    inputData.data[0].sector_aggregation.map((sector: any) => {
      returnData.push({
        sector: sector.sector_name,
        gross_exposure_pct: sector.aggregate_pct_gross_exposure,
        pl_pct: sector.aggregate_pct_pl,
      });
    });

    return returnData;
  }
};

// the purpose of this function is to calculate domains for the two y-axis that keep the 0 line in the same place
// this requires some trickery
// I find the absolutte min/max of each domain, and make the positive side and negative sides of the domain equal to it.
// I then multiply those values by 1.2 in order for there to be some visual buffer to the edge of the chart
const buildDomains = (
  inputData: DataObject,
): { left: number[]; right: number[] } => {
  // We need to ensure the values are always atleast 1%, recharts formats weirdly when they go below 1%
  const left = [-1, 1];
  const right = [-1, 1];

  if (inputData.data && inputData.data.length) {
    inputData.data[0].sector_aggregation.map((sector: any) => {
      if (sector.aggregate_pct_gross_exposure < left[0]) {
        left[0] = sector.aggregate_pct_gross_exposure;
      } else if (sector.aggregate_pct_gross_exposure > left[1]) {
        left[1] = sector.aggregate_pct_gross_exposure;
      }
      if (sector.aggregate_pct_pl < right[0]) {
        right[0] = sector.aggregate_pct_pl;
      } else if (sector.aggregate_pct_pl > right[1]) {
        right[1] = sector.aggregate_pct_pl;
      }
    });
  }

  const leftAbsMax = Math.max(Math.abs(left[0]), Math.abs(left[1]));
  const rightAbsMax = Math.max(Math.abs(right[0]), Math.abs(right[1]));

  return {
    left: [-(leftAbsMax * 1.2), leftAbsMax * 1.2],
    right: [-(rightAbsMax * 1.2), rightAbsMax * 1.2],
  };
};

const ExposureVsCumulativeChart: React.FC<ExposureVsCumulativeChartProps> = ({
  data,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const builtChartData = buildChartData(data);

  const domains = buildDomains(data);

  const id = `Initial-Exposure-vs-Cumulative-Returns-Sector-Analysis`;
  const title = `Initial Exposure vs Cumulative Returns - Sector Analysis`;

  const { ref, handleDownload } = usePngFromRecharts();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: id,
        handler: handleDownload,
        type: PdfComponentType.LINE_CHART,
        title: title,
      }),
    );
  }, [ref]);

  const saveImage = async () => {
    const imageData = await handleDownload();
    if (imageData) {
      FileSaver.saveAs(imageData.data, `${id}.png`);
    }
  };

  return (
    <GridItem xs={12} card>
      <div className={classes.toolbar}>
        <h2 className={classes.cardTitle}>{title}</h2>
        <div className={classes.exportButtons}>
          <ExportButton
            fileName={`${id}.csv`}
            exportData={builtChartData}
            fields={Object.keys(builtChartData[0])}
            fieldsMap={[
              { key: 'sector', label: 'Sector' },
              { key: 'gross_exposure_pct', label: 'Gross Exposure' },
              { key: 'pl_pct', label: 'P/L' },
            ]}
          />
          <ChartDownloadButton handler={saveImage} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          width={500}
          height={400}
          data={builtChartData}
          margin={{
            top: 20,
            right: 50,
            left: 30,
            bottom: 20,
          }}
          ref={ref}
        >
          <CartesianGrid strokeDasharray="5 5" horizontal={false} />
          <XAxis
            dataKey="sector"
            interval={0}
            angle={-25}
            textAnchor="end"
            height={100}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
          >
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '125%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              }}
              value={'Sector'}
              position={'insideBottom'}
            />
          </XAxis>
          <YAxis
            yAxisId="left"
            orientation="left"
            width={70}
            tickFormatter={(tickItem) => {
              return percentageToNdecialPlaces(tickItem / 100, 1);
            }}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
            domain={domains.left}
          >
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '125%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              }}
              angle={270}
              value={'Gross Exposure %'}
              position={'insideLeft'}
            />
          </YAxis>
          <YAxis
            yAxisId="right"
            orientation="right"
            width={70}
            tickFormatter={(tickItem) => {
              return percentageToNdecialPlaces(tickItem / 100, 1);
            }}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
            domain={domains.right}
          >
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '125%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              }}
              angle={270}
              value={'P/L %'}
              position={'insideRight'}
            />
          </YAxis>
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine yAxisId="left" y={0} stroke={greys.grey600} />
          <Bar
            yAxisId="left"
            dataKey="gross_exposure_pct"
            fill={mainColors.mainBlue}
          />
          <Bar yAxisId="right" dataKey="pl_pct" shape={CustomPLBar} />
        </BarChart>
      </ResponsiveContainer>
    </GridItem>
  );
};

export default ExposureVsCumulativeChart;
